import api from './client';

// Matching API
export const matchingAPI = {
  getAll: (status) => api.get('/matches', { params: { status } }),
  getDetails: (params) => api.get('/matches/details', { params }),
  // Manual-match picker candidates: SAME eligibility rules (date scope,
  // settled-invoice exclusion, review gate) as the auto-matcher's own
  // candidate pool -- see services/matching_candidates.py.
  getCandidateInvoices: (transactionId) =>
    api.get('/matches/candidate-invoices', { params: { transaction_id: transactionId } }),
  confirm: (id) => api.put(`/matches/${id}/confirm`),
  revertByInvoice: (invoiceId) => api.put(`/matches/invoice/${invoiceId}/revert`),
  // Partial-payment slice: unambiguous release by match id, needed once an
  // invoice can carry more than one confirmed allocation (revertByInvoice
  // 409s in that case and names this instead).
  revertByMatchId: (matchId) => api.put(`/matches/${matchId}/revert`),
  // Human write-off of a partially_matched invoice's remaining gap --
  // never automatic, amount/reason are audit-grade (services/
  // invoice_allocations.py::build_accept_residual_update).
  acceptResidual: (invoiceId, amount, reason) =>
    api.put(`/matches/invoice/${invoiceId}/accept-residual`, { amount, reason }),
  partialAllocationsSummary: () => api.get('/matches/partial-allocations-summary'),
  reject: (id) => api.put(`/matches/${id}/reject`),
  // Grouped-card "reject all": every suggested candidate for one transaction.
  rejectAllForTransaction: (transactionId) => api.put(`/matches/transaction/${transactionId}/reject-all`),
  // Operator-driven match: creates then confirms via the same confirm path.
  manualMatch: (transactionId, invoiceId) =>
    api.post('/matches/manual', { transaction_id: transactionId, invoice_id: invoiceId }),
  // High Confidence bulk-confirm (Commit 2): preview is read-only, the
  // operator reviews it, then POSTs the same match_ids back to confirm.
  highConfidencePreview: (entityId) =>
    api.get('/matches/high-confidence-preview', { params: { entity_id: entityId } }),
  bulkConfirmHighConfidence: (matchIds) =>
    api.post('/matches/bulk-confirm-high-confidence', { match_ids: matchIds }),
  // Operator-driven bulk confirm: the operator checked these rows themselves
  // in the Suggestions queue (no auto-computed eligibility set involved).
  bulkConfirmSelected: (matchIds) =>
    api.post('/matches/bulk-confirm-selected', { match_ids: matchIds }),
  // Preview/Apply backfill: stamps payment status on invoices confirmed before this wiring existed.
  previewPaymentBackfill: () => api.post('/admin/diagnostics/confirmed-match-payment-backfill', { dry_run: true }),
  applyPaymentBackfill: () => api.post('/admin/diagnostics/confirmed-match-payment-backfill', { dry_run: false }),
  // Match-Event Backfill (Diagnostics card): reconstructs missing confirm/
  // group_confirm events (services/match_events.py) for matches confirmed
  // before that write path existed -- reconstructed=True, never a live event.
  previewMatchEventBackfill: () => api.post('/admin/diagnostics/match-event-backfill', { dry_run: true }),
  applyMatchEventBackfill: () => api.post('/admin/diagnostics/match-event-backfill', { dry_run: false }),
  // Preview/Apply sweep: supersedes suggested matches that fail the current
  // matching gates (date scope, settled invoice, amount-agreement for
  // Medium/High) but were persisted before those gates existed.
  previewStaleSuggestedMatchesSweep: () =>
    api.post('/admin/diagnostics/stale-suggested-matches-sweep', { dry_run: true }),
  applyStaleSuggestedMatchesSweep: () =>
    api.post('/admin/diagnostics/stale-suggested-matches-sweep', { dry_run: false }),
  // BR OS Evidence Tier (Diagnostics card): Preview reports the full
  // would-auto-confirm list (Wise exact-transfer-identity candidates) for
  // one-time review, Apply arms auto-confirm for the next scheduled
  // auto-matching run -- not a repeated write endpoint (CLAUDE.md).
  previewBrosEvidenceTier: () =>
    api.post('/admin/diagnostics/bros-evidence-tier', { dry_run: true }),
  // tier: "wise_exact" (default) | "unambiguous" -- Preview always reports
  // both tiers regardless of this value; it only controls which flag Apply arms.
  applyBrosEvidenceTier: (tier = 'wise_exact') =>
    api.post('/admin/diagnostics/bros-evidence-tier', { dry_run: false, tier }),
  // Receipt-Paid Reconciliation (Diagnostics card): Preview/Apply the
  // "Paid-but-pending-verification" tool-subscription-receipt auto-verifier
  // (services/receipt_paid_reconciliation.py). Also runs on the same
  // schedule as ordinary auto-matching, so a manual Apply just runs it now.
  // Prompt S item 1: 202+JobStore -- the per-cross-currency-candidate FX
  // lookup on top of the bounded 200-row scan occasionally cleared the
  // proxy's ~30s window in prod. One job collection, two operation keys
  // ("preview"/"apply") selected by dry_run; job-latest reads dry_run back
  // to pick which operation's latest run to report.
  // previewedInvoiceIds (Prompt AC, Apply-only): preview.reconciled[].
  // invoice_id from the Preview that produced the count on the Apply
  // button, so the backend can tell "reconciled by a concurrent pass since
  // Preview ran" apart from a silent drop -- see receipt_paid_reconciliation.py.
  receiptPaidReconciliationStart: (dryRun, previewedInvoiceIds) =>
    api.post('/admin/diagnostics/receipt-paid-reconciliation/start', {
      dry_run: dryRun, previewed_invoice_ids: previewedInvoiceIds || [],
    }),
  receiptPaidReconciliationJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/receipt-paid-reconciliation/job-status/${jobId}`),
  receiptPaidReconciliationJobLatest: (dryRun) =>
    api.get('/admin/diagnostics/receipt-paid-reconciliation/job-latest', { params: { dry_run: dryRun } }),
  // Standing count of suggested-only matches awaiting a human confirm --
  // independent of any single Preview/Apply pass, see the route's own docstring.
  receiptPaidAwaitingReviewCount: () =>
    api.get('/admin/diagnostics/receipt-paid-reconciliation/awaiting-review-count'),
  // Suggested (pending review) bucket (ReceiptPaidReconciliationPanel):
  // enriched rows for the same "scan, don't auto-confirm" standing
  // population the awaiting-review-count above counts. confirmSuggested /
  // bulkConfirmSuggested are the sanctioned human path through confirm_
  // match's own review-status gate -- they approve the invoice's review
  // (routes/review.py::review_invoice, by reference) THEN confirm the
  // match, both scoped server-side to this exact provenance. The ordinary
  // reject (below) is reused as-is for dismiss: it never touches the
  // invoice's review_status, only the Match doc.
  suggestedPendingReviewList: () => api.get('/matches/suggested-pending-review'),
  confirmSuggested: (matchId) => api.put(`/matches/${matchId}/confirm-suggested`),
  bulkConfirmSuggested: (matchIds) =>
    api.post('/matches/bulk-confirm-suggested', { match_ids: matchIds }),
  // Vendor-text cohort recompute: repairs a Suggested (pending review) match
  // the vendor-text arm wrote before its own one-to-one cohort solver
  // existed (services/receipt_paid_reconciliation.py::recompute_suggested_
  // pending_review_vendor_text_cohorts). Preview reports what Apply would
  // change without writing; Apply performs the repair. Bounded/synchronous,
  // same <=200-row population as suggestedPendingReviewList above.
  suggestedVendorTextCohortRecomputePreview: () =>
    api.post('/matches/suggested-vendor-text-cohort-recompute/preview'),
  suggestedVendorTextCohortRecomputeApply: () =>
    api.post('/matches/suggested-vendor-text-cohort-recompute/apply'),
  // Suggestion census/repair/hygiene: chunked chains (Prompt, 2026-08-26
  // v2) -- every population scan below moved off a single synchronous
  // call (timing out in prod at 2,651+ docs) onto services/chunked_
  // chain.py, same budget-boxed/resumable/chain-status shape as
  // receiptPaidReconciliationStart above and the auto-match chain. Each
  // pass is chainStart (returns job_id) + jobStatus poll (2s interval,
  // reuse useJobPolling) + chainStatus (survives a page reload / the gap
  // between one run finishing and its successor starting) + chainStop.
  suggestedCensusChainStart: () => api.post('/matches/suggested-pending-review-shape-census/chain-start'),
  suggestedCensusChainStatus: () => api.get('/matches/suggested-pending-review-shape-census/chain-status'),
  suggestedCensusChainStop: (chainId) => api.post('/matches/suggested-pending-review-shape-census/chain-stop', { chain_id: chainId }),

  suggestedReparsePrecedenceRepairPreviewChainStart: () =>
    api.post('/matches/suggested-reparse-precedence-repair/preview/chain-start'),
  suggestedReparsePrecedenceRepairPreviewChainStatus: () =>
    api.get('/matches/suggested-reparse-precedence-repair/preview/chain-status'),
  suggestedReparsePrecedenceRepairPreviewChainStop: (chainId) =>
    api.post('/matches/suggested-reparse-precedence-repair/preview/chain-stop', { chain_id: chainId }),
  suggestedReparsePrecedenceRepairApplyChainStart: () =>
    api.post('/matches/suggested-reparse-precedence-repair/apply/chain-start'),
  suggestedReparsePrecedenceRepairApplyChainStatus: () =>
    api.get('/matches/suggested-reparse-precedence-repair/apply/chain-status'),
  suggestedReparsePrecedenceRepairApplyChainStop: (chainId) =>
    api.post('/matches/suggested-reparse-precedence-repair/apply/chain-stop', { chain_id: chainId }),

  suggestedBacklogHygieneMeasurementChainStart: () =>
    api.post('/matches/suggested-backlog-hygiene-measurement/chain-start'),
  suggestedBacklogHygieneMeasurementChainStatus: () =>
    api.get('/matches/suggested-backlog-hygiene-measurement/chain-status'),
  suggestedBacklogHygieneMeasurementChainStop: (chainId) =>
    api.post('/matches/suggested-backlog-hygiene-measurement/chain-stop', { chain_id: chainId }),

  suggestedDuplicateSupersedePreviewChainStart: () =>
    api.post('/matches/suggested-duplicate-supersede/preview/chain-start'),
  suggestedDuplicateSupersedePreviewChainStatus: () =>
    api.get('/matches/suggested-duplicate-supersede/preview/chain-status'),
  suggestedDuplicateSupersedePreviewChainStop: (chainId) =>
    api.post('/matches/suggested-duplicate-supersede/preview/chain-stop', { chain_id: chainId }),
  suggestedDuplicateSupersedeApplyChainStart: () =>
    api.post('/matches/suggested-duplicate-supersede/apply/chain-start'),
  suggestedDuplicateSupersedeApplyChainStatus: () =>
    api.get('/matches/suggested-duplicate-supersede/apply/chain-status'),
  suggestedDuplicateSupersedeApplyChainStop: (chainId) =>
    api.post('/matches/suggested-duplicate-supersede/apply/chain-stop', { chain_id: chainId }),

  suggestedOrphanCloseoutPreviewChainStart: () =>
    api.post('/matches/suggested-orphan-closeout/preview/chain-start'),
  suggestedOrphanCloseoutPreviewChainStatus: () =>
    api.get('/matches/suggested-orphan-closeout/preview/chain-status'),
  suggestedOrphanCloseoutPreviewChainStop: (chainId) =>
    api.post('/matches/suggested-orphan-closeout/preview/chain-stop', { chain_id: chainId }),
  suggestedOrphanCloseoutApplyChainStart: () =>
    api.post('/matches/suggested-orphan-closeout/apply/chain-start'),
  suggestedOrphanCloseoutApplyChainStatus: () =>
    api.get('/matches/suggested-orphan-closeout/apply/chain-status'),
  suggestedOrphanCloseoutApplyChainStop: (chainId) =>
    api.post('/matches/suggested-orphan-closeout/apply/chain-stop', { chain_id: chainId }),

  suggestedLegacyNullMappingPreviewChainStart: () =>
    api.post('/matches/suggested-legacy-null-mapping/preview/chain-start'),
  suggestedLegacyNullMappingPreviewChainStatus: () =>
    api.get('/matches/suggested-legacy-null-mapping/preview/chain-status'),
  suggestedLegacyNullMappingPreviewChainStop: (chainId) =>
    api.post('/matches/suggested-legacy-null-mapping/preview/chain-stop', { chain_id: chainId }),
  suggestedLegacyNullMappingApplyChainStart: () =>
    api.post('/matches/suggested-legacy-null-mapping/apply/chain-start'),
  suggestedLegacyNullMappingApplyChainStatus: () =>
    api.get('/matches/suggested-legacy-null-mapping/apply/chain-status'),
  suggestedLegacyNullMappingApplyChainStop: (chainId) =>
    api.post('/matches/suggested-legacy-null-mapping/apply/chain-stop', { chain_id: chainId }),

  suggestedChainJobStatus: (jobId) => api.get(`/matches/suggested-chain-job-status/${jobId}`),
  suggestedChainJobLatest: (operation) => api.get(`/matches/suggested-chain-job-latest/${operation}`),
  // Auto-confirm tier (Arm B): deterministic tier-1-only qualification over
  // the same Suggested (pending review) bucket above -- confirms through
  // the SAME confirm-suggested writer, never a parallel mechanism (services/
  // suggested_match_autoconfirm.py). Flag default OFF; status carries the
  // flag state plus the last completed Apply run's counts/breakdown.
  suggestedAutoconfirmStatus: () => api.get('/matches/suggested-autoconfirm/status'),
  setSuggestedAutoconfirmEnabled: (enabled) =>
    api.post('/matches/suggested-autoconfirm/set-flag', { enabled }),
  // dry_run=true is Preview (read-only, lists qualifying rows with evidence
  // and non-qualifying rows with their one failing condition); dry_run=false
  // is Apply / "Drain now" / "Run Now" -- all the same job body.
  suggestedAutoconfirmStart: (dryRun) =>
    api.post('/matches/suggested-autoconfirm/start', { dry_run: dryRun }),
  suggestedAutoconfirmJobStatus: (jobId) =>
    api.get(`/matches/suggested-autoconfirm/job-status/${jobId}`),
  suggestedAutoconfirmJobLatest: (dryRun) =>
    api.get('/matches/suggested-autoconfirm/job-latest', { params: { dry_run: dryRun } }),
  // Receipt-Paid Job Inspector (Diagnostics card, Prompt AI): read-only
  // mid-pass stall observability -- the live job doc (heartbeat ring/
  // current receipt+stage from services.receipt_paid_reconciliation.
  // _PassHeartbeat) for both preview/apply operations, plus the loop-lag
  // watchdog's recent culprit frames. releaseJob is Preview (dryRun=true,
  // reports what WOULD be released, no write) / Apply (dryRun=false,
  // confirm-gated by the panel) -- marks a stuck running job stale so
  // is_operation_running releases it without waiting the 900s guard.
  receiptPaidJobInspector: () => api.get('/admin/diagnostics/receipt-paid-job-inspector'),
  receiptPaidJobInspectorRelease: (operation, dryRun) =>
    api.post('/admin/diagnostics/receipt-paid-job-inspector/release', { operation, dry_run: dryRun }),
  // Inflow Reference Matching (Diagnostics card): Preview/Apply for client
  // payments quoting a BR OS invoice number in their bank reference
  // (services/inflow_reference_matching.py). Same 202+JobStore shape and
  // rationale as receipt-paid reconciliation above (Prompt S item 1); also
  // runs on the same auto-matching schedule.
  inflowReferenceMatchingStart: (dryRun) =>
    api.post('/admin/diagnostics/inflow-reference-matching/start', { dry_run: dryRun }),
  inflowReferenceMatchingJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/inflow-reference-matching/job-status/${jobId}`),
  inflowReferenceMatchingJobLatest: (dryRun) =>
    api.get('/admin/diagnostics/inflow-reference-matching/job-latest', { params: { dry_run: dryRun } }),
  recentInflowReferenceMatches: () =>
    api.get('/admin/diagnostics/inflow-reference-matching/recent'),
  // Prompt H read-first field investigation (490dea47): which field
  // actually carries the client-facing Xero invoice number for a
  // source="bros" invoice. Read-only, browser-manual by design -- the
  // Diagnostics card just gives it a button instead of requiring DevTools.
  fieldSampleInflowReferenceMatching: () =>
    api.get('/admin/diagnostics/inflow-reference-matching/field-sample'),
  // Accounting-Reconciled Evidence Tier (Diagnostics card): same Preview/
  // Apply shape, reads db.accounting_reconciliation (services/
  // accounting_reconciliation.py's Pennylane pull) instead of BR OS's own
  // payment_match block.
  previewAccountingReconciledEvidenceTier: () =>
    api.post('/admin/diagnostics/accounting-reconciled-evidence-tier', { dry_run: true }),
  applyAccountingReconciledEvidenceTier: () =>
    api.post('/admin/diagnostics/accounting-reconciled-evidence-tier', { dry_run: false }),
  // Portfolio-Reconciled Evidence Tier (Diagnostics card): same Preview/
  // Apply shape, bridges the portfolio payment-marking scheduler's own
  // applies (routes/admin_pennylane_recon_preview.py::_apply_recon_match_core)
  // into the Entities reconciliation scope (routes/
  // admin_portfolio_reconciled_evidence_tier.py).
  previewPortfolioReconciledEvidenceTier: () =>
    api.post('/admin/diagnostics/portfolio-reconciled-evidence-tier', { dry_run: true }),
  applyPortfolioReconciledEvidenceTier: () =>
    api.post('/admin/diagnostics/portfolio-reconciled-evidence-tier', { dry_run: false }),
  // BROS Paid Re-entry (Diagnostics card): Preview reports what would
  // re-enter the candidate pool (BR OS's own "paid" boolean, no Finance-
  // Agent-confirmed payment), Apply arms it for the next auto-match chain
  // run. Tallies is the ongoing read-only per-run breakdown once active.
  previewBrosPaidReentry: () =>
    api.post('/admin/diagnostics/bros-paid-reentry', { dry_run: true }),
  applyBrosPaidReentry: () =>
    api.post('/admin/diagnostics/bros-paid-reentry', { dry_run: false }),
  brosPaidReentryTallies: () =>
    api.get('/admin/diagnostics/bros-paid-reentry/tallies'),
  // Match Lifecycle Probe (Diagnostics card): read-only, per-invoice/per-
  // transaction reconstruction of the full suggest/confirm/sweep history.
  // Accepts an invoice number, invoice id, or transaction id.
  matchLifecycleProbe: (query) =>
    api.get('/admin/diagnostics/match-lifecycle-probe', { params: { query } }),
  // Write Provenance Probe (Diagnostics card): read-only, per-transaction
  // reconstruction of every timestamped trace the app's own collections
  // hold for who wrote to it and when (match_events, reconciliation_memory,
  // heal/ownership-conflict logs, sync_logs + auto-match chain runs in a
  // caller-provided window, and any audit-shaped fields on the transaction
  // document itself). windowStart/windowEnd are optional ISO overrides.
  writeProvenance: (transactionId, windowStart, windowEnd) =>
    api.get('/admin/diagnostics/write-provenance', {
      params: { transaction_id: transactionId, window_start: windowStart || undefined, window_end: windowEnd || undefined },
    }),
  // Preview/Apply backfill: stamps category on already-reconciled transactions confirmed before
  // the shared confirm-time categorization helper existed (services/match_categorization.py).
  // Both steps are out-of-band JobStore runs (Preview's group-match scan since 9f37be06 is the
  // same cohort-scaling shape a 105-row Apply already 504'd on) -- start each and poll its own
  // job-status/job-latest pair.
  previewCategoryBackfillStart: () => api.post('/admin/diagnostics/confirmed-match-category-backfill/preview/start'),
  previewCategoryBackfillJobLatest: () => api.get('/admin/diagnostics/confirmed-match-category-backfill/preview/job-latest'),
  applyCategoryBackfillStart: () => api.post('/admin/diagnostics/confirmed-match-category-backfill/apply/start'),
  categoryBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/confirmed-match-category-backfill/job-status/${jobId}`),
  categoryBackfillJobLatest: () => api.get('/admin/diagnostics/confirmed-match-category-backfill/job-latest'),
  // Reconciliation Pointer Heal (Diagnostics card): evidence-driven repair
  // of the pointer-without-flag anomaly (routes/admin_reconciliation_
  // pointer_heal.py) -- Preview reports healable/ambiguous/unverified
  // buckets with per-row evidence_classes, Apply restores is_reconciled
  // only on the healable bucket. Same request bundles the confirm-stepper
  // self-heal half server-side; this client call surfaces both halves of
  // the response, the pointer-heal half is what the Diagnostics card renders.
  previewReconciliationPointerHeal: () =>
    api.post('/admin/diagnostics/reconciliation-pointer-heal', { dry_run: true }),
  applyReconciliationPointerHeal: () =>
    api.post('/admin/diagnostics/reconciliation-pointer-heal', { dry_run: false }),
  // Confirm-Stepper Self-Heal (Diagnostics card): repairs a transaction/
  // invoice pair where confirm_match's step 2 (transaction reconciled)
  // completed but step 3 (invoice matching_status="matched") never did --
  // a crashed or never-re-made confirm call (routes/admin_confirm_stepper_
  // self_heal.py). Preview lists every stuck pair, Apply resumes each one
  // through confirm_match's own idempotent stepper.
  previewConfirmStepperSelfHeal: () =>
    api.post('/admin/diagnostics/confirm-stepper-self-heal', { dry_run: true }),
  applyConfirmStepperSelfHeal: () =>
    api.post('/admin/diagnostics/confirm-stepper-self-heal', { dry_run: false }),
  // BR OS Allocation Backfill (Diagnostics card): corrects the group
  // payment_match.allocated_amount corruption where BR OS's own
  // reconciliation engine stamped every sibling invoice in a multi-invoice
  // Wise payment with the PARENT payment's claimed total instead of that
  // invoice's own itemized share (routes/admin_bros_allocation_backfill.py).
  // Preview is read-only and lists every correctable group's per-sibling
  // corrections plus any uncorrectable ones (signature matches but doesn't
  // reconcile -- surfaced, never guessed). Apply backfills the correctable
  // groups; re-run Preview afterward to confirm it settles to 0.
  previewBrosAllocationBackfill: () =>
    api.post('/admin/diagnostics/bros-allocation-backfill', { dry_run: true }),
  applyBrosAllocationBackfill: () =>
    api.post('/admin/diagnostics/bros-allocation-backfill', { dry_run: false }),
  // FR Auto-Finalize status + flag toggle (Diagnostics card): the
  // pipeline's finalize step is governed by a single DB-backed flag
  // (routes/admin_auto_finalize_scheduler.py) that applies to the manual
  // trigger, finalize-apply, and the day-2 scheduled job alike.
  getAutoFinalizeSchedulerStatus: () =>
    api.get('/admin/diagnostics/auto-finalize-scheduler-status'),
  setAutoFinalizeEnabled: (enabled) =>
    api.post('/admin/diagnostics/auto-finalize-scheduler-set-flag', { enabled }),
  // Auto-matching (CLAUDE.md section 13): budget-boxed chunked chain, same
  // engine as the admin diagnostics chains -- each run time-boxes itself and
  // self-schedules a successor from the persisted cursor/tallies until the
  // unreconciled-transaction cohort converges, instead of one synchronous
  // full transaction x invoice cross-product per click.
  autoMatchChainStart: (entityId) =>
    api.post('/matches/auto-match/chain-start', null, { params: { entity_id: entityId } }),
  autoMatchChainStatus: (entityId) =>
    api.get('/matches/auto-match/chain-status', { params: { entity_id: entityId } }),
  autoMatchChainStop: (chainId) =>
    api.post('/matches/auto-match/chain-stop', { chain_id: chainId }),
  // Force-recovery for a chain stuck "running" with no successor ever
  // picking it up (e.g. an entity-scoped chain, which chain_watchdog does
  // not auto-reclaim). Backend only acts once the chain is provably
  // stalled -- see routes/matching.py::restart_auto_match_chain.
  autoMatchChainRestart: (entityId) =>
    api.post('/matches/auto-match/chain-restart', null, { params: { entity_id: entityId } }),
  autoMatchJobStatus: (jobId) => api.get(`/matches/auto-match/job-status/${jobId}`),
  autoMatchJobLatest: (entityId) =>
    api.get('/matches/auto-match/job-latest', { params: { entity_id: entityId } }),
  // Match Funnel (explain-yourself instrument): pure point-read of the
  // latest auto-match chain's persisted stage tallies + samples -- never
  // recomputes/walks a collection itself. Start/continue the chain (above)
  // to get fresh numbers.
  matchFunnel: (entityId) => api.get('/matches/match-funnel', { params: { entity_id: entityId } }),
  // Group match: one transaction settling several invoices in a single
  // confirmation event (routes/matching.py::confirm_group_match and
  // siblings). manualGroupMatch creates then confirms in one call, same
  // create-then-confirm shape as manualMatch above.
  groupSuggestion: (transactionId) =>
    api.get('/matches/group-suggestions', { params: { transaction_id: transactionId } }),
  manualGroupMatch: (transactionId, invoiceIds) =>
    api.post('/matches/group/manual', { transaction_id: transactionId, invoice_ids: invoiceIds }),
  confirmGroup: (matchGroupId) => api.put(`/matches/group/${matchGroupId}/confirm`),
  revertGroup: (matchGroupId) => api.put(`/matches/group/${matchGroupId}/revert`),
};

// CWC statement-payout reconciliation: Consistency Census + Preview/Apply
// card (see services/statement_payout_matcher.py).
export const statementPayoutMatchingAPI = {
  getCensus: () => api.get('/statement-payouts/census'),
  getPreview: () => api.get('/statement-payouts/preview'),
  apply: (pairs = null) => api.post('/statement-payouts/apply', { pairs }),
  // Repair matches stuck at status="confirmed" whose transaction was never
  // (re-)stamped reconciled -- invisible to the ordinary preview/apply flow
  // above, see backend route docstring (services/statement_payout_matcher.py).
  // Part-reconciliation of payouts whose amount does NOT equal the
  // statement's net profit: proposals carry a signed variance and an
  // overpayment/underpayment label (services/statement_payout_matcher.py::
  // build_partial_payout_proposals). Pairs beyond the tolerance ratio come
  // back under out_of_tolerance and are never auto-applied.
  getPartialPreview: () => api.get('/statement-payouts/partial-preview'),
  applyPartial: (pairs = null) => api.post('/statement-payouts/partial-apply', { pairs }),
  repairPreview: () => api.get('/statement-payouts/repair-preview'),
  repairApply: () => api.post('/statement-payouts/repair-apply'),
  // Releases a single ORPHANED match (transaction_id no longer resolves,
  // e.g. hard-deleted by a mock-data/bank-account purge, or statement_id no
  // longer resolves live) to a terminal released_* status -- never back to
  // "suggested" (see services/statement_payout_matcher.py::release_orphaned_match).
  repairRelease: (matchId) => api.post('/statement-payouts/repair-release', { match_id: matchId }),
};

// Recurring-cost reconciliation: Consistency Census + Preview/Apply card,
// coverage grid, and Bank Transactions row enrichment (see
// services/recurring_cost_matcher.py, services/recurring_cost_match_chain.py).
// Census/Preview read the latest COMPLETE chain -- budget-boxed chunked
// chain over the candidate-transaction walk (CLAUDE.md section 13), same
// engine as matchingAPI's auto-match chain above. chain-start/status/stop
// + job-status/job-latest mirror that shape 1:1.
export const recurringCostMatchingAPI = {
  getCensus: () => api.get('/recurring-costs/matching/census'),
  getPreview: () => api.get('/recurring-costs/matching/preview'),
  apply: (pairs = null) => api.post('/recurring-costs/matching/apply', { pairs }),
  getConfirmed: () => api.get('/recurring-costs/matching/confirmed'),
  getCoverageGrid: (months = 6) => api.get('/recurring-costs/coverage-grid', { params: { months } }),
  runNow: () => api.post('/recurring-costs/matching/run-now'),
  chainStart: () => api.post('/recurring-costs/matching/chain-start'),
  chainStatus: () => api.get('/recurring-costs/matching/chain-status'),
  chainStop: (chainId) => api.post('/recurring-costs/matching/chain-stop', { chain_id: chainId }),
  jobStatus: (jobId) => api.get(`/recurring-costs/matching/job-status/${jobId}`),
  jobLatest: () => api.get('/recurring-costs/matching/job-latest'),
  // Repair matches stuck at status="confirmed" with an incomplete confirm
  // step (transaction never stamped reconciled/categorized) -- these are
  // invisible to the ordinary matcher/apply flow, see backend route docstring.
  repairPreview: () => api.get('/recurring-costs/matching/repair-preview'),
  repairApply: () => api.post('/recurring-costs/matching/repair-apply'),
  // Releases a single ORPHANED match (transaction_id no longer resolves,
  // e.g. hard-deleted by a mock-data/bank-account purge) back to
  // status="suggested" so its instance reopens for future matching.
  repairRelease: (matchId) => api.post('/recurring-costs/matching/repair-release', { match_id: matchId }),
};

// Reconciliation memory: learned counterparty -> target prior shared by
// all three matchers (see services/reconciliation_memory.py).
export const reconciliationMemoryAPI = {
  getStats: () => api.get('/reconciliation-memory/stats'),
  getAll: (targetKind) => api.get('/reconciliation-memory', { params: targetKind ? { target_kind: targetKind } : {} }),
  unlearn: (memoryId, { decrementOnly = false } = {}) =>
    api.delete(`/reconciliation-memory/${memoryId}`, { params: { decrement_only: decrementOnly } }),
};

// Statement Orphan Repoint: the write path for the Orphaned Links
// population above -- a separate card/endpoint on purpose, since the
// census card is read-only by design. Preview never writes; Apply
// re-points statement_payout_matches (+ the mirrored transactions.
// matched_statement_id) onto each orphan's surviving twin and recomputes
// reconciliation on the touched target statement(s).
export const statementOrphanRepointAPI = {
  preview: () => api.get('/admin/statement-orphan-repoint/preview', { timeout: 30000 }),
  apply: () => api.post('/admin/statement-orphan-repoint/apply', null, { timeout: 30000 }),
  // Manual pick for one ambiguous (or zero-confident-candidate) orphan --
  // the radio-per-candidate picker. Refuses with 409 if the chosen
  // statement isn't among the match's current candidates.
  resolve: (matchId, targetStatementId) => api.post(
    '/admin/statement-orphan-repoint/resolve',
    { match_id: matchId, target_statement_id: targetStatementId },
    { timeout: 30000 },
  ),
};

// Bank Account Adoption: rewires a reconnected/provider-switched OB account
// (new connection, new provider account id) onto its existing bank_accounts
// identity (entity mapping, transaction history) instead of leaving it as a
// silent duplicate. Preview never writes; Apply only ever adopts a pair with
// exactly one strong (IBAN or unmasked sort-code+account-number) match;
// Resolve is the manual radio-per-candidate pick for anything ambiguous or
// masked-only. connectionId scopes the "live" side to one connection -- the
// Integrations page's per-connection "Adopt accounts" action.
export const accountAdoptionAPI = {
  preview: (connectionId) => api.get('/admin/account-adoption/preview', {
    params: connectionId ? { connection_id: connectionId } : undefined,
    timeout: 30000,
  }),
  apply: (connectionId) => api.post('/admin/account-adoption/apply', null, {
    params: connectionId ? { connection_id: connectionId } : undefined,
    timeout: 30000,
  }),
  resolve: (liveAccountId, targetAccountId) => api.post(
    '/admin/account-adoption/resolve',
    { live_account_id: liveAccountId, target_account_id: targetAccountId },
    { timeout: 30000 },
  ),
};
